<template>
  <div class="bqp">
    <InlineNotice v-if="preview" variant="info" icon="fas fa-flask">
      Aperçu : tous les questionnaires de qualification actifs sont affichés. Sur une vraie demande,
      seuls ceux liés à la formation choisie apparaissent.
    </InlineNotice>

    <div class="bqp-list">
      <section v-for="questionnaire in questionnaires" :key="questionnaire.id" class="bqp-block">
        <header class="bqp-block-head">
          <h4 class="bqp-block-name">{{ questionnaire.name }}</h4>
          <Badge variant="info" size="xs">{{ questionCountLabel(questionnaire) }}</Badge>
        </header>
        <p v-if="questionnaire.description" class="bqp-block-desc">{{ questionnaire.description }}</p>

        <div class="bqp-questions">
          <div v-for="question in questionsPosees(questionnaire)" :key="question.id">
            <BesoinFieldError :error="errors[`q-${question.id}`]" :section="section">
              <ValidateInfoQuestionRenderer
                :question="avecEtoile(question)"
                :answer="getAnswer(question.id)"
                :disabled="disabled"
                interne
                @update="(value) => $emit('update-answer', question.id, value, question.type)"
                @update-commentaire="(texte) => $emit('update-commentaire', question.id, texte)"
              />
            </BesoinFieldError>
            <!-- Le client peut avoir donné cette réponse dans sa demande de
                 devis, ou l'avoir corrigée depuis son questionnaire
                 d'organisation : le signaler évite de croire à une saisie du
                 commercial. -->
            <p v-if="isAnswerFromClient(question.id)" class="bqp-source">
              <i class="fas fa-user-pen" aria-hidden="true"></i>
              Réponse donnée par le client
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
/**
 * LES QUESTIONS DE QUALIFICATION DE LA FORMATION, posées pendant l'appel.
 *
 * Les questionnaires de qualification rattachés à la formation choisie — jusqu'à
 * quatre sur les formations incendie. Le client les retrouvera préremplis dans
 * son questionnaire d'organisation, après signature : même questionnaire, même
 * donnée.
 *
 * Ils se posent dans la section « Formation », sous le nombre de participants
 * (retour Figma du 2026-09-11) : ils avaient auparavant leur propre section,
 * « Validation des modalités », loin de la formation qu'ils qualifient. Le
 * tunnel ne monte le panneau que s'il y a au moins un questionnaire.
 *
 * PRÉSENTATIONNEL : des props entrent, `update-answer` sort.
 */
import { Badge, InlineNotice } from '../ui'
import ValidateInfoQuestionRenderer from '../validation/ValidateInfoQuestionRenderer.vue'
import { exigeeDansLeTunnel } from '../../composables/quotes/useBesoinMissing.js'
import BesoinFieldError from '../validation/BesoinFieldError.vue'

defineProps({
  questionnaires: {
    type: Array,
    default: () => []
  },
  /** Section confirmée ou devis signé : les réponses ne se modifient plus. */
  disabled: {
    type: Boolean,
    default: false
  },
  preview: {
    type: Boolean,
    default: false
  },
  /** Accès à une réponse enregistrée, fourni par useQuoteBesoinForm. */
  getAnswer: {
    type: Function,
    required: true
  },
  /**
   * Les questions réellement posées d'un questionnaire — condition comprise.
   * Fournie par `useQuoteBesoinForm`, comme `getAnswer` : le panneau reste
   * présentationnel et ne va pas relire les réponses pour décider quoi montrer.
   */
  questionsPosees: {
    type: Function,
    required: true
  },
  isAnswerFromClient: {
    type: Function,
    required: true
  },
  /**
   * Erreur par question (`q-<id>`), après un refus de confirmer la section :
   * les réponses obligatoires manquantes passent en rouge.
   */
  errors: {
    type: Object,
    default: () => ({})
  },
  /** Section du tunnel qui porte les questions (repère du premier champ en erreur). */
  section: {
    type: String,
    default: 'formation'
  }
})

defineEmits(['update-answer', 'update-commentaire'])

/**
 * L'étoile suit ce que le tunnel exige, et non le seul drapeau du catalogue :
 * une question à choix y est toujours obligatoire. Une copie, pour ne rien
 * changer à la question partagée avec le questionnaire du client.
 */
const avecEtoile = (question) => (
  exigeeDansLeTunnel(question) && !question.required ? { ...question, required: true } : question
)

const questionCountLabel = (questionnaire) => {
  const n = (questionnaire.questions || []).length
  return n > 1 ? `${n} questions` : `${n} question`
}
</script>

<style scoped>
.bqp {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

.bqp-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.bqp-block {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.bqp-block-head {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.bqp-block-name {
  margin: 0;
  font-size: var(--font-size-md, 1rem);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.bqp-block-desc {
  margin: 0;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.bqp-questions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.bqp-source {
  /* Pas de marge négative : remontée, la mention chevauchait la dernière
     réponse de la question — elle s'affiche désormais sous chaque réponse
     donnée dans la demande de devis. */
  margin: 0 0 var(--spacing-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}
</style>
